import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import ImageApi from '../../../api/image';
import { TranslateApi } from '../../../api';
import ClockIcon from '../../../assets/images/clock.svg';
import CalendarIcon from '../../../assets/images/calendar.svg';
import LocationIcon from '../../../assets/images/location.svg';
import ParticipantsIcon from '../../../assets/images/attendees.svg';

import ShowAllParticipantsModal from '../../ShowAllEventParticipantsModal';
import { logFirebaseEvent } from '../../../firebase/analytics';
import FeedItemFooterControl from '../../Feed/FeedItem/components/FeedItemFooterControl';

import EventDetailsItemHeader from '../components/EventDetailsHeader';
import { RoundedSwitch, Portal } from '../../../components';

const EventDetailsItem = ({ event, eventGoing, eventNotGoing, updateEvent, toggleCommentLike, toggleLike, postComment, userImageUrl, getFeedItemDetails, addAlertToAlerts, currentUser }) => {
  const navigate = useNavigate();
  const [image, setImage] = useState('');
  const [showAllParticipants, setShowAllParticipants] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);

  const [eventTitle, setEventTitle] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDescription, setEventDescription] = useState('');

  const itemType = 'event'
  const startDate = moment(event?.post?.start_time);
  const currentTime = new Date();
  const endTime = event?.post && new Date(event?.post?.end_time);
  const isPassed = event?.post && event?.post?.end_time !== null ? currentTime - endTime : -1;

  useEffect(() => {
    if (event?.post?.image && event?.post?.image?.url) {
      ImageApi.getImage(event?.post?.image?.url).then((imageUrl) => {
        setImage(imageUrl);
      });
    }
  }, [event?.post?.image]); 

    useEffect(() => {
      setEventTitle(event?.post?.title);
      setEventLocation(event?.post?.location);
      setEventDescription(event?.post?.description);
  }, [event?.post]); 

  const renderTime = (startTime, endTime) => {
    const startDate = moment(startTime);
    const endDate = moment(endTime);
    const sameDay = endTime && startDate.isSame(endDate, 'day');


    const elements = [];
    elements.push(startDate.format('HH:mma'));
    if (!sameDay) {
      elements.push(<div key="splitter1" className="oval" />);
      elements.push(startDate.format('dddd'));
    }
    if (endTime) {
      elements.push(endDate.format(' - HH:mma'));
      elements.push(<div key="splitter2" className="oval" />);
      elements.push(endDate.format('dddd'));
    }

    return <div className="event-container-text pl-3 d-flex align-items-center">{elements}</div>;
  };

  const renderDates = (startTime, endTime) => {
    const startDate = moment(startTime);
  
    return (
      <p className="event-container-text pl-3">
        {startDate.format('Do MMMM')}
      </p>
    );
  };

  const handleEvent = (type) => {
    if (type) {
      eventGoing(event.postable_id);
      logFirebaseEvent({
        event: 'events',
        action: 'going',
      });
    } else {
      eventNotGoing(event.postable_id);
      logFirebaseEvent({
        event: 'events',
        action: 'not_going',
      });
    }
  };

  const getDetails = (postableId, type) => {
      getFeedItemDetails({ type: itemType, id: postableId });
    };
  
  const postCommentEvent = (postId, postableId, postType, commentText) => {
      logFirebaseEvent({
        event: postType,
        action: 'add_comment',
      });
      postComment({ postType: itemType, comment: commentText, postId, postableId });
    };

  const onToggleLike = (postId, postableId, postType, newLikeState) => {
      logFirebaseEvent({
        event: postType,
        action: newLikeState ? 'like' : 'unlike',
      });
      toggleLike({ postId, postableId, postType, newLikeState });
    };

  const onToggleCommentLike = (postId, postableId, postType, commentId, newLikeState) => {
    logFirebaseEvent({
      event: 'comments',
      action: 'like',
    });
    toggleCommentLike({ postId, postableId, postType, newLikeState, commentId });
  };

  const onTranslateClicked = async () => {
    let translatedTitle, translatedLocation, translatedDescription;
    
    setIsTranslated(!isTranslated)

    if (isTranslated) {
      setEventTitle(event?.post?.title);
      setEventLocation(event?.post?.location);
      setEventDescription(event?.post?.description);

      return null
    }

    if (currentUser.userData.language_id) {
      const resultTitle = await TranslateApi.getTranslation(currentUser.userData.language_id, eventTitle)
      if (resultTitle && resultTitle.success) {
        translatedTitle = resultTitle.data
      }

      const resultLocation = await TranslateApi.getTranslation(currentUser.userData.language_id, eventLocation)
      if (resultLocation && resultLocation.success) {
        translatedLocation = resultLocation.data
      }

      const resultDescription = await TranslateApi.getTranslation(currentUser.userData.language_id, eventDescription)
      if (resultDescription && resultDescription.success) {
        translatedDescription = resultDescription.data
      }

      if (!translatedLocation || !translatedDescription || !translatedTitle) {
        addAlertToAlerts({
          title: 'Message',
          message: 'Translation to your selected language is not currently available.',
          confirmBtnText: 'Okay',
        });
      } else {
        setEventTitle(translatedTitle);
        setEventLocation(translatedLocation);
        setEventDescription(translatedDescription);
      }
    } else{
      addAlertToAlerts({
        title: 'Message',
        message: 'User does not have a language configured.',
        confirmBtnText: 'Okay',
      });
    }
  }

  return (
    <div className="event-container-inner">
      {event && event.post ? (
        <EventDetailsItemHeader
          createdAt={event.post?.created_at}
          author={event.post?.author}
          postId={event.id}
          eventId={event.post?.id}
          goBack={() => navigate(-1)}
          updateEvent={updateEvent}
        />
      ) : null}
      <div className="p-3">
        {image ? (
          <img className="event-container-image" src={image} alt="community-thumbnail" />
        ) : null}
        <p className="event-container-title mt-4">{eventTitle}</p>
        <p className="event-container-text mt-2 pb-5">
          {eventDescription?.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </p>
        <p className="event-container-title">Details</p>
        {event?.post?.location ? (
          <>
            <div className="event-container-contact">
              <img width="28" height="28" src={LocationIcon} alt="location icon" />
              <p className="event-container-text pl-3">{eventLocation}</p>
            </div>
            <div className="event-container-divider" />
          </>
        ) : null}
         <div className="event-container-contact">
           <img width="28" height="28" src={CalendarIcon} alt="calendar icon" />
            {renderDates(event?.post?.start_time, event?.post?.end_time)}
         </div>

         <div className="event-container-divider" />

         <div className="event-container-contact">
            <img width="28" height="28" src={ClockIcon} alt="clock icon" />
            {renderTime(event?.post?.start_time, event?.post?.end_time)}
         </div>

         <div className="event-container-divider" />

         <span className="event-container-contact cursor-pointer" role="button" onClick={() => setShowAllParticipants(true)}>
            <img width="28" height="28" src={ParticipantsIcon} alt="participants icon" />
            {event?.post?.participants.length > 1 && <img width="28" height="28" src={ParticipantsIcon} alt="participants icon" />}
            <p className="event-container-text pl-3 ">{`${event?.post?.participants.length} attending`}</p>
         </span>
         <div className="event-container-divider" />
            <Portal
              hide={() => setShowAllParticipants(false)}
              isOpen={showAllParticipants}
            >
              <ShowAllParticipantsModal
                participants={event?.post?.participants}
                hide={() => setShowAllParticipants(false)}
                title={`${event?.post?.participants.length} Attending`}
              />
            </Portal>
          </div>
      {isPassed < 0 ? (
        <div className="event-visiting-container d-flex justify-content-end pr-3 pt-4 pb-3">
          <RoundedSwitch
            updateChecked={handleEvent}
            checked={event?.post?.is_going}
            textOff="Can't go"
            textOn="Going"
          />
        </div>
      ) : null}
      <div className="feed-item-footer w-100 d-flex justify-content-end m-0 p-0 align-items-center" style={{cursor: 'pointer', right: 25, paddingTop: 20}} onClick={onTranslateClicked}>
        <p className="m-0 p-0 pr-2">{`${isTranslated ? "Show original" : "Translate this"}`}</p>
      </div>
      {event && event.id ? (
        <FeedItemFooterControl
          comments={event.comments}
          onComment={() => getDetails(event.postable_id, itemType)}
          isPostSingle={true}
          link={`/events/${event.postable_id}?type=${itemType}`}
          postComment={(commentText) =>
            postCommentEvent(event.id, event.postable_id, itemType, commentText)
          }
          userImageUrl={userImageUrl}
          isLiked={!!event?.post?.is_liked_by_me}
          onLike={() => {
            onToggleLike(
              event?.id,
              event?.postable_id,
              itemType,
              !event?.post.is_liked_by_me,
            )
          }}
          onCommentLike={(commentId, isLikeByMe) => onToggleCommentLike(event.id, event.postable_id, itemType, commentId, isLikeByMe)}
          disableButtons={false}
          isMandatoryGroup={false}
        />
      ) : (
        null
      )}
    </div>
  );
};

export default EventDetailsItem;
